import axios from 'axios'
import { i18n } from '../../../plugins/i18n'
import {
  crossAnalysisTypes,
  displayAllPrefix, generateGraphId,
  gmbInsightDataTypes,
  periods
} from '../../../modules/analysis'
import { fibonacciRetryPromise } from '../../../modules/retryPromise'
import cloneDeep from 'lodash.clonedeep'

export const companiesAnalysisInsight = {
  namespaced: true,
  state: {
    plots: {},                   // API で取得した生のグラフデータ
    rawAggs: [],                    // API で取得したテーブルデータ
    aggsLoading: false,                         // テーブルデータ取得中かどうか
    plotsLoading: false,                        // グラフデータ取得中かどうか
    dataType: gmbInsightDataTypes[0].data[0],                       // データ種別
    graphKey: [],
    date: periods[1],            // 日, 週, 月
    displayGraphs: [],              // グラフ表示対象
    pageType: crossAnalysisTypes[0],// 分析軸
    crossSearchField: null,         // クロス分析
    individualAnalysisField: null,  // テーブル表示リスト（= ドリルダウン対象のテーブルの行のfield）
    noLinked: false,                // GMB 連携されているかどうか
    availableDataTypes: [],         // 利用可能な検索条件（データの種類）, gmbInsightDataTypes.data[]
    aggsPolling: false,
  },
  getters: {
    /**
     * * 分析軸の項目一覧
     * @returns { Array }
     */
    pageTypes(state, getters, rootState) {
      return rootState.companiesAnalysisAxes.data.filter(v => !['site', 'locale'].includes(v.id))
    },
    /**
     * クロス分析の項目一覧
     * @param { Object } state
     * @returns { Array }
     */
    crossSearchFields(state) {
      const arr = [state.pageType]
      if (state.crossSearchField) {
        arr.push(state.crossSearchField)
      }
      return arr
    },
    /**
     * テーブル レコードID
     * @param { Object } state
     * @returns { String }
     */
    individualAnalysisId(state) {
      return state.individualAnalysisField ? state.individualAnalysisField.id : null
    },
    /**
     * テーブル レコード名
     * @param { Object } state
     * @returns { String }
     */
    individualAnalysisName(state) {
      return state.individualAnalysisField ? state.individualAnalysisField.name : ''
    },
    /**
     * データ種別の項目一覧
     * @returns { Array }
     */
    dataTypes() {
      let dataTypes = []
      gmbInsightDataTypes.forEach(type => {
        dataTypes.push(type.data)
      })
      return Array.prototype.concat.apply([], dataTypes)
    },
    /**
     * aggs API に渡すパラメータ
     * @param { Object } state
     * @returns { Object }
     */
    aggsParams: state => {
      return {
        analysis_axis: state.pageType.fields,
        cross_analysis_axis: state.crossSearchField?.fields,
      }
    },
    /**
     * plots API に渡すパラメータ
     * @param { Object } state
     * @returns { Object }
     */
    plotsParams: state => data => {
      return {
        analysis_axis: data.fields[0].key,
        analysis_axis_id: data.fields[0].id,
        cross_analysis_axis: data.fields[1]?.key,
        cross_analysis_axis_id: data.fields[1]?.id,
        period: state.date.id,  // ex. 'week'
      }
    },
    /**
     * export API に渡すパラメータ
     * @param { Object } state
     * @param { Boolean } value
     * @return { Void }
     */
    exportParams: state => {
      return {
        analysis_axis: state.pageType.fields,
        cross_analysis_axis: state.crossSearchField?.fields,
        period: state.date.id, // ex. 'week'
      }
    },
    /**
     * テーブルデータ
     * @param { Object } state
     * @param { Object } getters
     * @returns { Array }
     */
    aggs(state, getters) {
      if (state.rawAggs.length === 0) return state.rawAggs

      let aggs = JSON.parse(JSON.stringify(state.rawAggs))

      if(getters.individualAnalysisId) {
        // 個別分析idでaggsを絞り込み
        aggs = aggs.filter(agg => {
          const currentAnalysisAxisField = agg.fields.find(field => field.key === state.pageType.fields)
          return currentAnalysisAxisField.id === getters.individualAnalysisId
        })
      }

      // テーブルの「全」系のfieldsを作成
      const fields = aggs[0].fields.map(field => {
        return {
          id: 0,
          key: field.key,
          name: i18n.t('models.analysis.crossAnalysisTypes.allName')
        }
      })

      if(getters.individualAnalysisId) {
        // テーブル「表示リスト全て」の列の値を変更
        fields[0].name = i18n.t('models.analysis.displayAll')
        fields[0].id = getters.individualAnalysisId
        fields[0].key = displayAllPrefix + fields[0].key
      }

      // aggの合計を計算して入れる
      const businessImpressions = aggs.reduce((acc, current) => acc + Number(current.businessImpressions), 0)
      const businessImpressionsDesktopSearch = aggs.reduce((acc, current) => acc + Number(current.businessImpressionsDesktopSearch), 0)
      const businessImpressionsMobileSearch = aggs.reduce((acc, current) => acc + Number(current.businessImpressionsMobileSearch), 0)
      const businessImpressionsDesktopMaps = aggs.reduce((acc, current) => acc + Number(current.businessImpressionsDesktopMaps), 0)
      const businessImpressionsMobileMaps = aggs.reduce((acc, current) => acc + Number(current.businessImpressionsMobileMaps), 0)
      const microConversions = aggs.reduce((acc, current) => acc + Number(current.microConversions), 0)
      const websiteClicks = aggs.reduce((acc, current) => acc + Number(current.websiteClicks), 0)
      const businessDirectionRequests = aggs.reduce((acc, current) => acc + Number(current.businessDirectionRequests), 0)
      const callClicks = aggs.reduce((acc, current) => acc + Number(current.callClicks), 0)
      const businessBookings = aggs.reduce((acc, current) => acc + Number(current.businessBookings), 0)
      const businessConversations = aggs.reduce((acc, current) => acc + Number(current.businessConversations), 0)
      const businessFoodOrders = aggs.reduce((acc, current) => acc + Number(current.businessFoodOrders), 0)

      const agg = {
        businessImpressions,
        businessImpressionsDesktopSearch,
        businessImpressionsMobileSearch,
        businessImpressionsDesktopMaps,
        businessImpressionsMobileMaps,
        microConversions,
        websiteClicks,
        businessDirectionRequests,
        callClicks,
        businessBookings,
        businessConversations,
        businessFoodOrders,
        fields: fields,
        storeNames: null,
      }
      aggs.unshift(agg)
      return aggs
    },
    /**
     * 選択中の行
     * @param { Object } state
     * @returns { Array }
     */
    selectedAggIds(state) {
      return state.displayGraphs
    },
    graphType(state) {
      return ['businessImpressions', 'microConversions'].includes(state.dataType.id) ? 'bar' : 'line'
    }
  },
  mutations: {
    /**
     * データ種別の変更
     * @param { Object } state
     * @param { String } dataType
     * @return { Void }
     */
    setDataType(state, value) {
      state.dataType = value
    },
    /**
     * 日, 週, 月の変更
     * @param { Object } state
     * @param { String } date
     * @return { Void }
     */
    setDate(state, value) {
      state.date = value
    },
    /**
     * グラフ表示対象の変更
     * @param { Object } state
     * @param { String } graphs
     * @return { Void }
     */
    setDisplayGraphs(state, graphs) {
      state.displayGraphs = graphs
    },
    /**
     * 分析軸の変更
     * @param { Object } state
     * @param { Object } axisObj
     * @return { Void }
     */
    setPageType(state, axisObj) {
      state.pageType = axisObj
    },
    /**
     * クロス分析軸の変更
     * @param { Object } state
     * @param { String } queryElement
     * @return { Void }
     */
    setCrossSearchField(state, value) {
      state.crossSearchField = value
    },
    /**
     * テーブル表示リストの変更
     * @param { Object } state
     * @param { String } field
     * @return { Void }
     */
    setIndividualAnalysisField(state, field) {
      state.individualAnalysisField = field
    },
    /**
     * @param { Object } state
     * @return { Void }
     */
    setInitialPlots(state) {
      state.plots = {}
    },
    /**
     * テーブルデータの取得
     * @param { Object } state
     * @return { Void }
     */
    readAggs(state) {
      state.aggsLoading = true
      state.plots = {}
      state.rawAggs = []
    },
    /**
     * テーブルデータの取得成功
     * @param { Object } state
     * @return { Void }
     */
    readAggsSuccess(state, data) {
      state.rawAggs = data.map(v => {
        v = v.attributes
        return {
          businessImpressions: v.business_impressions,
          businessImpressionsDesktopSearch: v.business_impressions_desktop_search,
          businessImpressionsMobileSearch: v.business_impressions_mobile_search,
          businessImpressionsDesktopMaps: v.business_impressions_desktop_maps,
          businessImpressionsMobileMaps: v.business_impressions_mobile_maps,
          microConversions: v.micro_conversions,
          websiteClicks: v.website_clicks,
          businessDirectionRequests: v.business_direction_requests,
          callClicks: v.call_clicks,
          businessBookings: v.business_bookings,
          businessConversations: v.business_conversations,
          businessFoodOrders: v.business_food_orders,
          fields: v.fields,
          storeNames: v.store_names,
        }
      })
      state.aggsLoading = false
    },
    /**
     * テーブルデータの取得失敗
     * @param { Object } state
     * @return { Void }
     */
    readAggsFailure(state) {
      state.aggsLoading = false
    },
    /**
     * グラフデータの取得
     * @param { Object } state
     * @return { Void }
     */
    readPlots(state) {
      state.plotsLoading = true
    },
    /**
     * グラフデータの取得成功
     * @param { Object } state
     * @param { Array } payload
     * @return { Void }
     */
    readPlotsSuccess(state, responses) {
      // ディープコピー
      const plots = cloneDeep(state.plots)

      const setPlots = res => {
        const data = res.data.data.attributes
        const params = res.config.params
        state.graphKey = Object.keys(data[state.dataType.dataKey])
        if (params.isRequestingDisplayAllPlot) {
          params.analysis_axis = displayAllPrefix + params.analysis_axis
          data.label = i18n.t('models.analysis.displayAll')
        }
        let id = params.analysis_axis + '-' + params.analysis_axis_id
        if (params.cross_analysis_axis) {
          id += '|' + params.cross_analysis_axis + '-' + params.cross_analysis_axis_id
        }
        plots[id] = { ...data }
      }

      if (Array.isArray(responses)) {
        responses.forEach(res => setPlots(res))
      } else {
        setPlots(responses)
      }

      state.plots = plots // objectは代入し直さないとリアクティブにならない
      state.plotsLoading = false
    },
    /**
     * グラフデータの取得失敗
     * @param { Object } state
     * @return { Void }
     */
    readPlotsFailure(state) {
      state.plotsLoading = false
    },
    /**
     * CSV データの取得
     * @param { Object } state
     * @return { Void }
     */
    exportCsv(state) {
      state.loadError = false
    },
    /**
     * CSV データの取得 成功
     * @param { Object } state
     * @return { Void }
     */
    exportCsvSuccess(state) {
      state.loading = false
      state.loadError = false
      state.errors = null
    },
    /**
     * CSV データの取得 失敗
     * @param { Object } state
     * @param { Object } errors
     * @return { Void }
     */
    exportCsvFailure(state, errors) {
      if (errors.response.status === 404) state.noLinked = true
      state.loading = false
      state.loadError = true
      state.errors = errors
    },
    /**
     * @param { Object } state
     * @param { Array } value
     * @return { Void }
     */
    setAvailableDataTypes(state, value) {
      if (value.length === 0) {
        return true
      }

      const target = Array.isArray(value) ? value[0] : value
      const dataKeys = Object.keys(target.data.data.attributes)

      state.availableDataTypes = gmbInsightDataTypes.map(group => {
        return {
          name: group.name,
          data: group.data.filter(v => dataKeys.includes(v.dataKey))
        }
      }).filter(group => group.data.length)

    },
    setPolling(state, value) {
      state.aggsPolling = value
    },
  },
  actions: {
    /**
     * データ種別の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setDataType({ commit }, payload) {
      commit('setDataType', payload)
    },
    /**
     * 日, 週, 月の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setDate({ commit }, payload) {
      commit('setDate', payload)
    },
    /**
     * グラフデータの初期化
     * @param { Function } commit
     * @return { Void }
     */
    setDisplayGraphs({ commit }, payload) {
      commit('setDisplayGraphs', payload)
    },
    setInitialPlots({ commit }) {
      commit('setInitialPlots')
    },
    /**
     * 分析軸の変更
     * @param { Function } commit
     * @param rootState
     * @param { String } queryElement
     * @return { Void }
     */
    setPageType({ commit, state, getters }, queryElement) {
      commit('setPageType', getters.pageTypes.find(v => v.fields === queryElement))
      if (queryElement === state.crossSearchField?.fields) {
        commit('setCrossSearchField', null)
      }
    },
    /**
     * クロス分析の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setCrossSearchField({ commit }, payload) {
      commit('setCrossSearchField', payload)
    },
    /**
     * テーブル表示リストの変更
     * @param { Function } commit
     * @param { Object } getters
     * @param { String } payload
     * @return { Void }
    */
    setIndividualAnalysisField({ commit }, payload) {
      commit('setIndividualAnalysisField', payload)
    },
    /**
     * テーブルデータ取得
     * @param { Function } commit
     * @param { Object } getters
     * @param { Object } payload
     * @return { Promise }
     */
    readAggs({ commit, getters, state }, payload) {
      const { companyUuid, params } = payload
      commit('readAggs')
      const retryFunc = () => {
        return axios
          .get(`/api/companies/${companyUuid}/analysis/gmb_insights/aggregates.json`, {
            headers: {
              'Content-Type': 'application/json'
            },
            params: { ...getters.aggsParams, ...params }
          })
      }

      const evalFunc = (result) => {
        const isPolling = result.data.status === 'processing'
        commit('setPolling', isPolling)
        return !isPolling
      }
      return fibonacciRetryPromise(retryFunc, evalFunc)
        .then(res => {
          commit('readAggsSuccess', res.data.data)
          if (state.displayGraphs.length === 0) {
            commit('setDisplayGraphs', [generateGraphId(getters.aggs[0])])
            return
          }

          let nextDisplayGraphs = getters.aggs.filter(agg => {
            return getters.selectedAggIds.includes(generateGraphId(agg))
          })
            // 再リクエストするグラフの順番を維持して、色が変わらないようにする
            .sort((a, b) => getters.selectedAggIds.findIndex(v => v === generateGraphId(a)) - getters.selectedAggIds.findIndex(v => v === generateGraphId(b)))
            .map(agg => generateGraphId(agg))
          if (nextDisplayGraphs.length === 0) {
            nextDisplayGraphs = [generateGraphId(getters.aggs[0])]
          }
          commit('setDisplayGraphs', nextDisplayGraphs)
        })
        .catch(err => {
          commit('readAggsFailure')
          console.error('error', err)
        })
    },
    /**
     * グラフデータ取得
     * @param { Function } commit
     * @param { Object } getters
     * @param { Object } payload
     * @return { Promise }
     */
    readPlots({ commit, state, getters }, payload) {
      const { companyUuid, params } = payload.params
      commit('readPlots')

      if (payload.parallel) {
        return axios
          .get(`/api/companies/${ companyUuid }/analysis/gmb_insights/graph_elements.json`, {
            headers: {
              'Content-Type': 'application/json'
            },
            params: {
              analysis_axis: state.pageType.fields,
              analysis_axis_id: 0,
              period: state.date.id,
              ...params,
            }
          })
          .then(responses => {
            commit('readPlotsSuccess', responses)
            commit('setAvailableDataTypes', responses)
          })
          .catch(err => {
            commit('readPlotsFailure')
            console.error('error', err)
          })
      } else {
        // まだ取得していないもののみ取得しに行く
        let graphIdList = getters.selectedAggIds.filter(id => {
          return !Object.prototype.hasOwnProperty.call(state.plots, id)
        })
        let filteredDisplayGraphs = getters.aggs.filter((field) => {
          return graphIdList.find((item) => {
            return item === generateGraphId(field)
          })
        })
        if (state.individualAnalysisField) {
          filteredDisplayGraphs = filteredDisplayGraphs.map(v => {
            v = cloneDeep(v)
            v.fields = v.fields.map(field => {
              if (field.key.includes(displayAllPrefix)) {
                v.isRequestingDisplayAllPlot = true
                field.key = field.key.replace(displayAllPrefix, '')
              }
              return field
            })
            return v
          })
        }
        const requests = filteredDisplayGraphs.map(graph => {
          return axios
            .get(`/api/companies/${ companyUuid }/analysis/gmb_insights/graph_elements.json`, {
              headers: {
                'Content-Type': 'application/json'
              },
              params: { ...getters.plotsParams(graph), ...params, isRequestingDisplayAllPlot: graph.isRequestingDisplayAllPlot }
            })
        })
        return Promise.all(requests)
          .then(responses => {
            commit('readPlotsSuccess', responses)
            commit('setAvailableDataTypes', responses)
          })
          .catch(err => {
            commit('readPlotsFailure')
            console.error('error', err)
          })
      }
    },
    /**
     * CSV データ取得
     * @param { Function } commit
     * @param { Object } state
     * @param { Object } payload
     * @return { Promise }
     */
    exportCsv({ commit, getters }, payload) {
      if (getters.aggs.length == 0) return false

      const { companyUuid, params } = payload
      commit('setIndividualAnalysisField', null)
      commit('exportCsv')
      return axios
        .post(`/api/companies/${ companyUuid }/export/analysis/gmb_insights.json`,
          { ...getters.exportParams, ...params }
        )
        .then(() => {
          commit('exportCsvSuccess')
          return true
        })
        .catch(err => {
          commit('exportCsvFailure')
          console.error('error', err)
          return false
        })
    }
  }
}
